<script setup lang="ts">
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUser, useAuth } from '@clerk/vue';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const route = useRoute();
const { user } = useUser();
const { signOut } = useAuth();
const { t } = useI18n();

const displayName = computed(() => user.value?.fullName || user.value?.username || '');
const userInitial = computed(() => {
  const name = user.value?.fullName || user.value?.username || 'A';
  return name.charAt(0).toUpperCase();
});

const currentPath = computed(() => route.path);

const menuItems = [
  { key: 'adminOrgs.title', path: '/admin/orgs' },
  { key: 'adminBilling.title', path: '/admin/billing' },
];

async function handleLogout() {
  await signOut.value();
  router.push('/');
}
</script>

<template>
  <div class="flex h-screen bg-gray-950">
    <!-- Sidebar -->
    <aside class="flex w-56 flex-col bg-gray-800 border-r border-gray-700">
      <div class="flex h-16 items-center px-4 border-b border-gray-700">
        <span class="text-sm font-bold text-white tracking-wide uppercase">⚙ LiteTrack Admin</span>
      </div>

      <nav class="flex-1 p-3 space-y-1">
        <!-- Back to app -->
        <button
          @click="router.push('/dashboard')"
          class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
        >
          <svg class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {{ t('nav.backToApp') }}
        </button>

        <div class="my-2 border-t border-gray-700" />

        <!-- Admin nav items -->
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          :class="[
            'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
            currentPath === item.path
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:bg-gray-700 hover:text-white',
          ]"
        >
          <svg class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          {{ t(item.key) }}
        </router-link>
      </nav>
    </aside>

    <!-- Main area -->
    <div class="flex flex-1 flex-col overflow-hidden">
      <!-- Header -->
      <header class="flex h-16 items-center justify-between bg-gray-900 border-b border-gray-700 px-6">
        <span class="text-sm font-semibold text-gray-400 uppercase tracking-widest">Admin Console</span>

        <div class="flex items-center gap-4">
          <div class="flex items-center gap-2">
            <div class="h-7 w-7 rounded-full bg-gray-700 flex items-center justify-center">
              <span class="text-xs font-medium text-gray-200">{{ userInitial }}</span>
            </div>
            <span class="text-sm text-gray-300">{{ displayName }}</span>
          </div>
          <button
            @click="handleLogout"
            class="text-sm text-gray-400 hover:text-white transition-colors"
          >
            {{ t('common.logout') }}
          </button>
        </div>
      </header>

      <main class="flex-1 overflow-auto p-6 bg-gray-950">
        <slot />
      </main>
    </div>
  </div>
</template>
